"use client"

import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useTheme } from './ThemeProvider';
import clsx from 'clsx';

// Variantes de animação para reutilização
const socialIconVariants = {
  initial: { y: 0, scale: 1 },
  hover: { y: -5, scale: 1.1 },
  tap: { scale: 0.9 }
};

const linkHoverVariants = {
  initial: { x: 0 },
  hover: { x: 5 }
};

// Tipagens para os props
interface SocialIconProps {
  href: string;
  ariaLabel: string;
  hoverColor?: string;
  children: ReactNode;
}

interface FooterLinkProps {
  href: string;
  label: string;
  children: ReactNode;
}

interface SectionTitleProps {
  id: string;
  children: ReactNode;
}

interface FooterSectionProps {
  title: string;
  titleId: string;
  children: ReactNode;
}

// Componente para ícones sociais
const SocialIcon = ({ href, ariaLabel, hoverColor = "hover:bg-blue-600", children }: SocialIconProps) => {
  const { theme } = useTheme();
  
  return (
    <motion.a 
      variants={socialIconVariants}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
      href={href} 
      className={clsx(
        "w-10 h-10 rounded-full flex items-center justify-center transition-all",
        hoverColor, "hover:text-white", 
        theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-700 text-gray-300'
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </motion.a>
  );
};

// Componente para links de serviços/empresa
const FooterLink = ({ href, label, children }: FooterLinkProps) => {
  const { theme } = useTheme();
  
  return (
    <li>
      <motion.a 
        variants={linkHoverVariants}
        initial="initial"
        whileHover="hover"
        href={href} 
        className={clsx(
          "transition-colors flex items-center",
          theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-400 hover:text-white'
        )}
        aria-label={label}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></span>
        {children}
      </motion.a>
    </li>
  );
};

// Componente para título de seção
const SectionTitle = ({ id, children }: SectionTitleProps) => {
  return (
    <h3 id={id} className="text-lg font-semibold mb-4 inline-flex items-center">
      <span className="w-1 h-6 bg-blue-500 mr-2 rounded-full"></span>
      {children}
    </h3>
  );
};

// Componente para seção de links
const FooterSection = ({ title, titleId, children }: FooterSectionProps) => {
  return (
    <div>
      <SectionTitle id={titleId}>{title}</SectionTitle>
      <ul className="space-y-3" aria-labelledby={titleId}>
        {children}
      </ul>
    </div>
  );
};

// Componente principal do Footer
const Footer = () => {
  const { theme } = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      role="contentinfo" 
      aria-labelledby="footer-title"
      className={clsx(
        "py-12 relative overflow-hidden",
        theme === 'dark' ? 'bg-gray-950 text-gray-100' : 'bg-gray-900 text-white'
      )}
    >
      {/* Título oculto para acessibilidade */}
      <h2 id="footer-title" className="sr-only">Informações do rodapé</h2>
      
      {/* Background gradient decoration */}
      <div className="absolute -top-40 -left-40 w-[40rem] h-[40rem] bg-blue-900/20 rounded-full filter blur-[100px] opacity-30" aria-hidden="true"></div>
      <div className="absolute -bottom-20 -right-20 w-[30rem] h-[30rem] bg-indigo-900/20 rounded-full filter blur-[100px] opacity-30" aria-hidden="true"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Seção de logo e descrição - agora ocupa 2 colunas */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex-shrink-0 block mb-4" aria-label="Página inicial da Web Imports">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center"
              >
                <div className="relative overflow-hidden logo-backdrop">
                  <div className="relative w-48 h-40 transition-all duration-300">
                    <Image 
                      src="/images/logo.png" 
                      alt="Web Imports - Eletrônicos & Tecnologia" 
                      width={180}
                      height={135}
                      className="object-contain"
                      priority
                    />
                  </div>
                </div>
              </motion.div>
            </Link>
            
            <p className={clsx(
              "mb-6 max-w-md",
              theme === 'dark' ? 'text-gray-300' : 'text-gray-300'
            )}>
              Somos especialistas em soluções tecnológicas completas, desde manutenção de dispositivos
              eletrônicos até desenvolvimento de sistemas personalizados e infraestrutura de rede.
            </p>
            
            {/* Redes Sociais */}
            <div className="flex items-center space-x-4 mb-6">
              <SocialIcon href="https://facebook.com" ariaLabel="Siga-nos no Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                </svg>
              </SocialIcon>
              
              <SocialIcon href="https://instagram.com" ariaLabel="Siga-nos no Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </SocialIcon>
              
              <SocialIcon 
                href="https://wa.me/5519994799400" 
                ariaLabel="Contate-nos pelo WhatsApp"
                hoverColor="hover:bg-green-600"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </SocialIcon>
            </div>
          </div>
          
          {/* Colunas de serviços e informações da empresa */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Seção de Desenvolvimento e Infraestrutura */}
            <FooterSection title="Desenvolvimento e Infraestrutura" titleId="footer-dev-infrastructure-title">
              <FooterLink href="/servicos/desenvolvimento-web" label="Desenvolvimento Web">
                Desenvolvimento Web
              </FooterLink>
              <FooterLink href="/servicos/desenvolvimento-sistemas" label="Desenvolvimento de Sistemas">
                Desenvolvimento de Sistemas
              </FooterLink>
              <FooterLink href="/servicos/infraestrutura-rede" label="Infraestrutura de Rede">
                Infraestrutura de Rede
              </FooterLink>
              <FooterLink href="/servicos/energia-solar" label="Energia Solar">
                Energia Solar
              </FooterLink>
              <FooterLink href="/servicos/automacao-residencial" label="Automação Residencial">
                Automação Residencial
              </FooterLink>
            </FooterSection>
            
            {/* Seção de Manutenção */}
            <FooterSection title="Manutenção" titleId="footer-maintenance-title">
              <FooterLink href="/servicos/manutencao-celulares-tablets" label="Celulares e Tablets">
                Celulares e Tablets
              </FooterLink>
              <FooterLink href="/servicos/manutencao-notebooks-pcs" label="Notebooks e PCs">
                Notebooks e PCs
              </FooterLink>
              <FooterLink href="/servicos/manutencao-videogames" label="Videogames">
                Videogames
              </FooterLink>
              <FooterLink href="/servicos/manutencao-eletronicos" label="Eletrônicos em Geral">
                Eletrônicos em Geral
              </FooterLink>
              <FooterLink href="/servicos/microsolda-especializada" label="Microsolda Especializada">
                Microsolda Especializada
              </FooterLink>
            </FooterSection>
            
            {/* Seção da Empresa e contato */}
            <div>
              {/* Empresa */}
              <FooterSection title="Empresa" titleId="footer-company-title">
                <FooterLink href="/sobre" label="Sobre Nós">
                  Sobre Nós
                </FooterLink>
                <FooterLink href="/projetos" label="Projetos">
                  Projetos
                </FooterLink>
                <FooterLink href="/blog" label="Blog">
                  Blog
                </FooterLink>
                <FooterLink href="/contato" label="Contato">
                  Contato
                </FooterLink>
              </FooterSection>
              
              {/* Contato dividido em subseções */}
              <div className="mt-8">
                <SectionTitle id="footer-contact-title">Contato</SectionTitle>
                <ul className="space-y-3" aria-labelledby="footer-contact-title">
                  <li>
                    <a href="tel:+551932060706" className="flex items-center group">
                      <span className="w-5 h-5 mr-2 text-blue-400 group-hover:text-blue-300">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </span>
                      (19) 3206-0706
                    </a>
                  </li>
                  <li>
                    <a href="https://wa.me/5519994799400" className="flex items-center group">
                      <span className="w-5 h-5 mr-2 text-green-400 group-hover:text-green-300">
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      </span>
                      (19) 99479-9400
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Seção de Endereço e Horário em uma única linha */}
        <div className="mt-12 pt-8 border-t border-gray-800 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex items-start">
            <span className="w-5 h-5 mr-3 text-blue-400 mt-1">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
            <div>
              <h4 className="font-medium text-white mb-1">Endereço</h4>
              <p className="text-gray-400">
                Rua Tapuru, 25<br />
                Alpha Mall / Loja 26<br />
                Alphaville, Campinas - SP, 13098-322
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <span className="w-5 h-5 mr-3 text-blue-400 mt-1">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <div>
              <h4 className="font-medium text-white mb-1">Horário de Funcionamento</h4>
              <p className="text-gray-400">
                Segunda - Sexta: 9h às 18h<br />
                Sábado: 9h às 13h
              </p>
            </div>
          </div>
        </div>
        
        {/* Copyright e links de política */}
        <div className={clsx(
          "mt-8 pt-6 border-t flex flex-col md:flex-row md:items-center md:justify-between",
          theme === 'dark' ? 'border-gray-800 text-gray-400' : 'border-gray-800 text-gray-500'
        )}>
          <p>© {currentYear} Web Imports. Todos os direitos reservados.</p>
          <div className="mt-4 md:mt-0 flex space-x-4">
            <a href="/privacidade" className="hover:text-white transition-colors">Política de Privacidade</a>
            <a href="/termos" className="hover:text-white transition-colors">Termos de Uso</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;